import { ref } from 'vue';

// Drag-drop wiring for a file drop zone, shared across the surfaces that stage
// attachments (Studio's chat column, the Home composer, the QuickAsk popover).
// Lifted from StudioView's chat-column drop so all surfaces behave identically:
//  - reacts ONLY to drags carrying file payloads (a text selection or a
//    draggable element is none of our business and must not pop the overlay),
//  - absorbs the dragenter/dragleave that fire for every inner-element crossing
//    via a depth counter (a naive boolean flickers),
//  - exposes `dragOver` to drive a drop overlay,
//  - funnels the dropped File[] into `onFiles`.
//
// Bind the four handlers + read `dragOver` on the element that should accept
// drops. `preventDefault` on dragover is what actually marks the element a
// valid drop target; without it the browser opens the file on release.
export function useFileDrop(onFiles: (files: File[]) => void) {
  const dragOver = ref(false);
  let depth = 0;

  function hasFiles(ev: DragEvent): boolean {
    return !!ev.dataTransfer && Array.from(ev.dataTransfer.types).includes('Files');
  }

  function onDragEnter(ev: DragEvent): void {
    if (!hasFiles(ev)) return;
    depth += 1;
    dragOver.value = true;
  }

  function onDragOver(ev: DragEvent): void {
    if (!hasFiles(ev)) return;
    ev.preventDefault();
    if (ev.dataTransfer) ev.dataTransfer.dropEffect = 'copy';
  }

  function onDragLeave(ev: DragEvent): void {
    if (!hasFiles(ev)) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0) dragOver.value = false;
  }

  function onDrop(ev: DragEvent): void {
    if (!hasFiles(ev)) return;
    ev.preventDefault();
    depth = 0;
    dragOver.value = false;
    const files = Array.from(ev.dataTransfer?.files ?? []);
    if (files.length) onFiles(files);
  }

  return { dragOver, onDragEnter, onDragOver, onDragLeave, onDrop };
}
